import { useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import ReviewCard from "@/components/ReviewCard";
import { Link } from "@/components/Link";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedInteractions } from "@/hooks/useFeedInteractions";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { categoryService, type CategoryFeedPage } from "@/api/categoryService";
import { experiencesCountLabel } from "@/lib/categoryLede";

// The category page's main view (quick 261010-oyg): the home-feed card (likes, comments, share,
// report) over the category's feed-eligible reviews, newest first, infinite scroll, plus a search
// box that searches those reviews semantically. The first page arrives server-rendered from
// +data.ts so the review text is in the HTML; a typed query swaps the list for ranked results
// and clearing it returns to the feed. SSR-safe: the observer and the debounce live in effects.

const PAGE_SIZE = 10;
const SEARCH_MIN_CHARS = 2;
const SEARCH_DEBOUNCE_MS = 400;

function FeedSkeletonCard() {
  return (
    <div className="catpage-feed-skeleton" aria-hidden="true">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <Skeleton className="h-9 w-9 rounded-full" />
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-3.5 w-24" style={{ marginBottom: 12 }} />
      <Skeleton className="h-3 w-full" style={{ marginBottom: 6 }} />
      <Skeleton className="h-3 w-11/12" style={{ marginBottom: 6 }} />
      <Skeleton className="h-3 w-2/3" style={{ marginBottom: 16 }} />
      <Skeleton className="h-8 w-40" />
    </div>
  );
}

export default function CategoryFeed({
  categorySlug,
  categoryTitle,
  initialPage,
  storesHref,
}: {
  categorySlug: string;
  categoryTitle: string;
  // Server-fetched first page; null when that fetch failed and the client loads from scratch.
  initialPage: CategoryFeedPage | null;
  storesHref: string;
}) {
  const { user } = useAuth();
  const { trackNavigation } = usePostHogTracking();

  // ── Feed ──
  const feedKey = useMemo(() => ["categoryFeed", categorySlug], [categorySlug]);
  const feedQuery = useInfiniteQuery({
    queryKey: feedKey,
    queryFn: ({ pageParam }) =>
      categoryService.getCategoryFeed(categorySlug, { cursor: pageParam, limit: PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor,
    initialData: initialPage ? { pages: [initialPage], pageParams: [undefined] } : undefined,
    // The server page is anonymous: a signed-in reader refetches on mount so their likes and
    // personal exclusions apply; anonymous readers keep the SSR page.
    staleTime: user ? 0 : 5 * 60 * 1000,
  });
  const feedReviews = feedQuery.data?.pages.flatMap((p) => p.reviews) ?? [];

  // ── Search ──
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);
  const searching = debounced.length >= SEARCH_MIN_CHARS;
  const searchKey = useMemo(
    () => ["categoryReviewSearch", categorySlug, debounced],
    [categorySlug, debounced]
  );
  const searchQuery = useQuery({
    queryKey: searchKey,
    queryFn: () => categoryService.searchCategoryReviews(categorySlug, debounced),
    enabled: searching,
    staleTime: 60 * 1000,
    // One retry: the first hybrid query after a quiet spell can time out while the embedder warms up.
    retry: 1,
  });
  const results = searchQuery.data?.reviews ?? [];

  // One analytics row per settled query, never per keystroke.
  const trackedQuery = useRef<string | null>(null);
  useEffect(() => {
    if (!searching || !searchQuery.data || trackedQuery.current === debounced) return;
    trackedQuery.current = debounced;
    trackNavigation.categoryReviewsSearched(categorySlug, debounced, searchQuery.data.reviews.length);
  }, [searching, searchQuery.data, debounced, categorySlug, trackNavigation]);

  // ── Interactions: one hook per cache the cards can live in ──
  const feedActions = useFeedInteractions({ queryKey: feedKey });
  const searchActions = useFeedInteractions({ queryKey: searchKey });

  // ── Infinite scroll: IntersectionObserver sentinel, feed mode only ──
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = feedQuery;
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || searching) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [searching, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const list = searching ? results : feedReviews;
  const actions = searching ? searchActions : feedActions;

  const searchStatus = !searching
    ? null
    : searchQuery.isPending
      ? "بندوّر…"
      : searchQuery.isError
        ? "البحث مش شغال دلوقتي، جرّب تاني."
        : results.length === 0
          ? `مفيش تجارب في ${categoryTitle} فيها «${debounced}». جرّب كلمة تانية.`
          : `${experiencesCountLabel(results.length)} فيها «${debounced}»، الأقرب لبحثك أولًا.`;

  return (
    <section className="catpage-feed" aria-labelledby="category-feed">
      <div className="catpage-sechead">
        <div>
          <h2 id="category-feed">{`تجارب المشترين في ${categoryTitle}`}</h2>
          <p>من تقييمات الأعضاء وتجارب جروبات فيسبوك، الأحدث أولًا.</p>
        </div>
      </div>

      <div className="catpage-search" role="search">
        <Search className="catpage-search-icon" aria-hidden="true" />
        <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setTerm("");
          }}
          placeholder={`دوّر في تجارب ${categoryTitle}…`}
          aria-label={`ابحث في تجارب ${categoryTitle}`}
          autoComplete="off"
        />
        {term && (
          <button type="button" className="catpage-search-clear" onClick={() => setTerm("")} aria-label="مسح البحث">
            <X aria-hidden="true" />
          </button>
        )}
      </div>
      {searchStatus && (
        <p className="catpage-search-status" aria-live="polite">
          {searchStatus}
        </p>
      )}

      <div className="catpage-feed-list">
        {list.map((review) => (
          <ReviewCard
            key={review._id}
            review={review}
            currentUserId={user?._id}
            feedStore={review.store}
            onLike={actions.like}
            onComment={actions.comment}
            onReport={actions.report}
            onNotInterestedReview={actions.hide}
            onMuteStore={actions.mute}
            onWatchStore={actions.watch}
            isLoading={actions.isPending}
          />
        ))}

        {!searching && feedQuery.isPending && (
          <>
            <FeedSkeletonCard />
            <FeedSkeletonCard />
            <FeedSkeletonCard />
          </>
        )}
        {searching && searchQuery.isPending && <FeedSkeletonCard />}
        {!searching && isFetchingNextPage && <FeedSkeletonCard />}
      </div>

      {!searching && feedQuery.isError && (
        <div className="catpage-feed-note">
          <p>مش قادرين نحمّل التجارب دلوقتي.</p>
          <button type="button" className="catpage-link" onClick={() => feedQuery.refetch()}>
            إعادة المحاولة
          </button>
        </div>
      )}

      {!searching && !feedQuery.isPending && !feedQuery.isError && feedReviews.length === 0 && (
        <div className="catpage-feed-note">
          <p>{`لسه مفيش تجارب منشورة في ${categoryTitle}.`}</p>
          <Link to={storesHref} className="catpage-link">
            شوف متاجر الفئة واكتب أول تجربة
          </Link>
        </div>
      )}

      {/* 1px, not 0: a zero-height target at the page's end can report no intersection (same as Dashboard). */}
      {!searching && <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />}

      {!searching && !hasNextPage && feedReviews.length > 0 && (
        <p className="catpage-feed-end">{`دي كل التجارب المنشورة في ${categoryTitle}.`}</p>
      )}
    </section>
  );
}
